import React, { useEffect, useState } from "react";
import { Clock, CheckCircle, AlertCircle } from "lucide-react";
import { ToastContainer, toast } from "react-toastify";
import '../index.css'
function AdminPanel({ onLogout }) {
  const [customizations, setCustomizations] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toasts, setToasts] = useState([]);
  const [activeTab, setActiveTab] = useState('customizations');

  const adminDetails = {
    name: "Admin User",
    email: "admin@example.com",
    role: "Super Administrator",
    lastLogin: new Date().toLocaleString(),
  };

  const showToast = (message, type = "success") => {
    const newToast = { id: Date.now(), message, type };
    setToasts((prev) => [...prev, newToast]);
    setTimeout(() => {
      setToasts((prev) => prev.filter((toast) => toast.id !== newToast.id));
    }, 3000);
  };

  const fetchCustomizations = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_SERVER_URL}/customization`);
      const data = await response.json();
      if (response.ok) {
        setCustomizations(data.map(item => ({
          ...item,
          isNew: true
        })));
      } else {
        showToast(data.message || "Failed to fetch customizations", "error");
      }
    } catch (error) {
      console.error(error);
      showToast("Network error while fetching customizations.", "error");
    }
  };

  const fetchContacts = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_SERVER_URL}/contactus`);
      const data = await response.json();
      if (response.ok) {
        setContacts(data.map(item => ({
          ...item,
          isNew: true
        })));
      } else {
        showToast(data.message || "Failed to fetch contacts", "error");
      }
    } catch (error) {
      console.error(error);
      showToast("Network error while fetching contacts.", "error");
    }
  };

  const handleMarkAsRead = (id, type) => {
    if (type === 'customization') {
      setCustomizations(prev => 
        prev.map(item => 
          item._id === id ? { ...item, isNew: false } : item
        )
      );
    } else {
      setContacts(prev => 
        prev.map(item => 
          item._id === id ? { ...item, isNew: false } : item
        )
      );
    }
    showToast("Marked as read successfully");
  };
  const handleLogout = async() => {
    const response = await fetch(`${process.env.REACT_APP_SERVER_URL}/logout`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Authorization" : `Bearer ${localStorage.getItem("adminToken")}`
      },
    });
    if(response.ok)
    {
      toast.success(`Succesfully LoggedOut`);
      localStorage.removeItem("adminToken"); // Remove the token
      localStorage.removeItem("adminLogin")
      window.location.href = "/login"; // Redirect to login page


    }else {
      toast.error(`Something went wrong try again after sometime`);
      console.log("Something went Wrong");
    }
  };

  useEffect(() => {
    setLoading(true);
    Promise.all([fetchCustomizations(), fetchContacts()]).then(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen bg-gray-100">
        <Clock className="animate-spin text-blue-600" size={48} />
        <p className="ml-4 text-xl text-gray-600">Loading data...</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col lg:flex-row min-h-screen bg-gray-100 font-sans">
          <ToastContainer position="top-center" autoClose={3000} />

      {/* Toast Notifications */}
      <div className="fixed bottom-4 right-4 space-y-2 z-50">
        {toasts.map((toast) => (
          <div
            key={toast.id}
            className={`p-4 rounded shadow-lg flex items-center ${
              toast.type === "error" 
                ? "bg-red-500 text-white" 
                : "bg-green-500 text-white"
            }`}
          >
            {toast.type === "error" ? <AlertCircle className="mr-2" /> : <CheckCircle className="mr-2" />}
            {toast.message}
          </div>
        ))}
      </div>

      {/* Sidebar */}
      <div className="w-full lg:w-64 bg-gradient-to-b from-blue-800 to-blue-900 text-white p-6 shadow-lg font-sans">
        <div className="mb-8 text-center">
          <h2 className="text-xl font-bold">{adminDetails.name}</h2>
          <p className="text-blue-200">{adminDetails.role}</p>
        </div>
        <div className="space-y-4">
          <button 
            onClick={() => setActiveTab('customizations')}
            className={`w-full py-2 rounded transition duration-300 ${
              activeTab === 'customizations' 
                ? 'bg-blue-600 text-white' 
                : 'bg-blue-700 text-blue-200 hover:bg-blue-600'
            }`}
          >
            Customization Requests
          </button>
          <button 
            onClick={() => setActiveTab('contacts')}
            className={`w-full py-2 rounded transition duration-300 ${
              activeTab === 'contacts' 
                ? 'bg-blue-600 text-white' 
                : 'bg-blue-700 text-blue-200 hover:bg-blue-600'
            }`}
          >
            Contact Submissions
          </button>
          <button
            onClick={handleLogout}
            className="w-full flex items-center justify-center bg-red-600 hover:bg-red-700 text-white py-2 rounded transition duration-300"
          >
            Logout
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 p-4 lg:p-8 font-sans">
        <h2 className="text-3xl font-bold text-gray-800 mb-6">Admin Dashboard</h2>
        
        {activeTab === 'customizations' && (
          <section className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-xl font-semibold mb-4">Customization Requests</h3>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead className="bg-gray-100">
                  <tr>
                    {['Phone', 'Email', 'Shape', 'Fluorescence', 'Carat', 'Clarity', 'Ratio', 'Color', 'Budget()', 'Message', 'Actions'].map(header => (
                      <th key={header} className="p-2 text-left">{header}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {customizations.map((item) => (
                    <tr 
                      key={item._id} 
                      className={item.isNew ? 'font-bold bg-blue-50' : ''}
                    >
                      <td className="p-2">{item.phone}</td>
                      <td className="p-2">{item.email}</td>
                      <td className="p-2">{item.shape}</td>
                      <td className="p-2">{item.fluorescence}</td>
                      <td className="p-2">{`${item.fromCarat}-${item.toCarat}`}</td>
                      <td className="p-2">{`${item.fromClarity}-${item.toClarity}`}</td>
                      <td className="p-2">{`${item.minRatio}-${item.maxRatio}`}</td>
                      <td className="p-2">{`${item.fromColor}-${item.toColor}`}</td>
                      <td className="p-2">{`${item.minBudget}-${item.maxBudget}`}</td>
                      <td className="p-2">{item.message}</td>
                      <td className="p-2">
                        <button
                          onClick={() => handleMarkAsRead(item._id, 'customization')}
                          className="text-blue-500 hover:text-blue-700 transition"
                        >
                          Mark as Read
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )}

        {activeTab === 'contacts' && (
          <section className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-xl font-semibold mb-4">Contact Us Submissions</h3>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead className="bg-gray-100">
                  <tr>
                    {['Name', 'Email', 'Phone', 'Subject', 'Message', 'Created On', 'Actions'].map(header => (
                      <th key={header} className="p-2 text-left">{header}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {contacts.map((item) => (
                    <tr 
                      key={item._id} 
                      className={item.isNew ? 'font-bold bg-blue-50' : ''}
                    >
                      <td className="p-2">{item.name}</td>
                      <td className="p-2">{item.email}</td>
                      <td className="p-2">{item.phone}</td>
                      <td className="p-2">{item.subject}</td>
                      <td className="p-2">{item.message}</td>
                      <td className="p-2">{new Date(item.createdOn).toLocaleString()}</td>
                      <td className="p-2">
                        <button
                          onClick={() => handleMarkAsRead(item._id, 'contact')}
                          className="text-blue-500 hover:text-blue-700 transition"
                        >
                          Mark as Read
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )}
      </div>
    </div>
  );
}

export default AdminPanel;